/* eslint-disable no-param-reassign */
import get from 'lodash/get';
import cookie from 'cookie';
import catchify from 'catchify';

import {
  USE_NEXT_BROWSE_EXPERIMENT_NAME,
  USE_NEXT_HOMEPAGE_EXPERIMENT_NAME,
  USE_NEXT_COLLECTIONS_EXPERIMENT_NAME,
  USE_NEXT_PDP_EXPERIMENT_NAME,
} from '@/constants/global';

import { isPDP } from '~/util/global';

const initialState = () => ({
  autocompleteQuery: '',
  autocompleteProductType: null,
  autocompleteGroup: null,
  session: {
    cart: {},
  },
  csrfToken: '',
  showBodyOverlay: false,
});

export { initialState as state };

const browseURLs = [
  '/browse/',
  '/search',
  '/software/',
  '/creative-resources/',
  '/templates/',
  '/courses-learning/'];

export const getters = {
  userSession: (state) => state.session.user,
  button: (state) => get(state, 'session.session.button', null),
  campaigns: (state) => get(state, 'session.session.campaigns', []),
  showBodyOverlay: (state) => state.showBodyOverlay,
};

export const mutations = {
  setCart(state, data) {
    state.session.cart = data;
  },

  setSession(state, data) {
    const [session, cookies] = data;
    state.session = session;

    // csrf cookie not created for some reason when hitting /browse as a anon new user.  Hack!
    // csrf needed for Experiments
    if (cookies) {
      state.csrfToken = cookies.find((c) => {
        const curCookie = cookie.parse(c);

        if (curCookie.csrftoken) {
          return c;
        }

        return false;
      }) || '';
    }
  },
  setAutocompleteQuery(state, data) {
    state.autocompleteQuery = data;
  },
  setAutocompleteProductType(state, data) {
    state.autocompleteProductType = data;
  },
  setAutocompleteGroup(state, data) {
    state.autocompleteGroup = data;
  },
  setUserAvatar(state, data) {
    state.session.user.avatar = data;
    state.session.user.avatar_small = `${data}?width=22&height=22&aspect_ratio=1:1`;
  },
  setShowBodyOverlay(state, data) {
    state.showBodyOverlay = data;
  },
  setMessages(state, data) {
    state.session.messages = data;
  },
};

// When the user visits a page nuxtServerInit runs on the server side which performs
// the initial enroll for global experiments and then serializes the resulting variants
// in the store across to the client.
//
// gtm-spa will handle re-issueing enrolls on navigation changes
export const actions = {
  async nuxtServerInit(context, { req, redirect }) {
    await context.dispatch('nextExperimentsRedirects', { req, redirect });
    await Promise.all([
      context.dispatch('browse/fetch'),
      context.dispatch('experiments/enroll'),
    ]);
  },
  // Since this is called by nuxtServerInit, then it also has the same behavior, is being called
  // on the server side, and it was also added to gtm-spa to be called on the client side
  // the parameters are validated so we can handle both server and client side calls
  async nextExperimentsRedirects(context, { req, redirect } = {}) {
    const url = req ? req.url : typeof window !== 'undefined' && window.location.pathname;
    const redirectFn = (localUrl) => {
      if (typeof window !== 'undefined') {
        window.location.href = localUrl;
      } else {
        redirect(localUrl);
      }
    };

    if (!url || !redirectFn) return Promise.resolve();

    const getExperimentVariant = context.getters['experiments/getExperimentVariant'];
    const isNextBrowseEnabled = getExperimentVariant(USE_NEXT_BROWSE_EXPERIMENT_NAME) === 'enabled';
    const isNextHomeEnabled = getExperimentVariant(USE_NEXT_HOMEPAGE_EXPERIMENT_NAME) === 'enabled';
    const isNextCollectionsEnabled = getExperimentVariant(USE_NEXT_COLLECTIONS_EXPERIMENT_NAME) === 'enabled';
    const isNextPDPEnabled = getExperimentVariant(USE_NEXT_PDP_EXPERIMENT_NAME) === 'enabled';

    if (url === '/' && isNextHomeEnabled) {
      return redirectFn(url);
    }

    if (browseURLs.some((x) => url.startsWith(x)) && isNextBrowseEnabled) {
      return redirectFn(url);
    }

    if (url.startsWith('/collections') && isNextCollectionsEnabled) {
      return redirectFn(url);
    }

    if (isPDP(url) && isNextPDPEnabled) {
      return redirectFn(url);
    }

    return Promise.resolve();
  },
  async updateCartCount({ commit }) {
    const { $axios, $router } = this;
    const params = {
      ...$router.query,
      format: 'json',
    };

    const [err, response] = await catchify($axios.get('/api/sessions/current/', { params }));

    if (err) {
      // eslint-disable-next-line no-console
      console.log('error fetching session for store', err);
      return;
    }

    commit('setCart', response.data.cart);
  },

  setShowBodyOverlay({ commit }, data) {
    commit('setShowBodyOverlay', data);
  },

  clearSessionMessages({ commit }) {
    commit('setMessages', []);
  },
};
